// Intent: the owner tracked family income for years in a Google Sheet
// ("Zarobki 2025": sources per person × months + notes + charts). This store
// moves that sheet into the app: income sources per ADULT household member,
// one entry per source per month, monthly prompt on the source's expected day.
// Data is local JSON-doc (auto-included in cloud/household sync via TABLES).

import { create } from 'zustand';

import { getDb, TABLES } from '@/db';
import { cancelIncomePrompt, scheduleIncomePrompt } from '@/notifications/local';
import { scheduleHouseholdSync } from '@/sync/household-sync';
import type { IncomeEntry, IncomeKind, IncomeSource } from '@/types/domain';
import { uuid } from '@/utils/format';

export interface NewSourceInput {
  memberId: string;
  name: string;
  kind: IncomeKind;
  expectedDay: number | null;
  expectedAmountGr: number | null;
}

// Pure derived helpers (React Compiler rule: never store getters).

/** 'YYYY-MM' of a Date. */
export function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function entryFor(entries: IncomeEntry[], sourceId: string, month: string): IncomeEntry | undefined {
  return entries.find((e) => e.sourceId === sourceId && e.month === month);
}

export function monthTotalGrOf(entries: IncomeEntry[], month: string): number {
  return entries.filter((e) => e.month === month).reduce((s, e) => s + e.amountGr, 0);
}

export function memberMonthTotalGrOf(
  sources: IncomeSource[],
  entries: IncomeEntry[],
  memberId: string,
  month: string,
): number {
  const ids = new Set(sources.filter((s) => s.memberId === memberId).map((s) => s.id));
  return entries.filter((e) => e.month === month && ids.has(e.sourceId)).reduce((s, e) => s + e.amountGr, 0);
}

/** Sum per member over the last N months (for the who-contributes pie). */
export function memberRangeTotalGrOf(
  sources: IncomeSource[],
  entries: IncomeEntry[],
  memberId: string,
  months: string[],
): number {
  const ids = new Set(sources.filter((s) => s.memberId === memberId).map((s) => s.id));
  return entries
    .filter((e) => months.includes(e.month) && ids.has(e.sourceId))
    .reduce((s, e) => s + e.amountGr, 0);
}

/** Last N month keys ending at `end` (inclusive), oldest first. */
export function lastMonths(n: number, end = new Date()): string[] {
  const out: string[] = [];
  const d = new Date(end.getFullYear(), end.getMonth(), 1);
  for (let i = n - 1; i >= 0; i--) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(monthKeyOf(m));
  }
  return out;
}

/** Sources past their expected day this month without an entry ("do uzupełnienia").
 *  Skipped months ("nie było wpływu") are excluded — one question per event,
 *  answered anywhere, silenced everywhere (same predicate as forecast.pending). */
export function overdueSourcesOf(sources: IncomeSource[], entries: IncomeEntry[], now = new Date()): IncomeSource[] {
  const month = monthKeyOf(now);
  const day = now.getDate();
  return sources.filter(
    (s) =>
      s.active &&
      s.expectedDay !== null &&
      day > s.expectedDay &&
      !(s.skippedMonths ?? []).includes(month) &&
      !entryFor(entries, s.id, month),
  );
}

/** Median of day-of-month values; null under 3 samples (too noisy to suggest). */
export function medianDayOf(days: number[]): number | null {
  if (days.length < 3) return null;
  const sorted = [...days].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

/**
 * "Wypłatę z X potwierdzasz zwykle ok. 12., nie 10. — przestawić?" — proposes a
 * new expectedDay when the CONFIRMATION day (entry.createdAt — we honestly don't
 * know the transfer day, entries are monthly) drifts ≥2 days for 3 months.
 * Dismissed day never returns (daySuggestionDismissed); AI proposes, human clicks.
 */
export function incomeDaySuggestionOf(source: IncomeSource, entries: IncomeEntry[]): number | null {
  if (!source.active || source.expectedDay === null) return null;
  const days = entries
    .filter((e) => e.sourceId === source.id)
    .sort((a, b) => b.month.localeCompare(a.month))
    .slice(0, 3)
    .map((e) => new Date(e.createdAt).getDate());
  const median = medianDayOf(days);
  if (median === null || median < 1 || median > 28) return null;
  if (Math.abs(median - source.expectedDay) < 2) return null;
  if (source.daySuggestionDismissed === median) return null;
  return median;
}

interface IncomeState {
  hydrated: boolean;
  sources: IncomeSource[];
  entries: IncomeEntry[];
  hydrate: () => Promise<void>;
  addSource: (input: NewSourceInput) => Promise<IncomeSource>;
  updateSource: (id: string, patch: Partial<NewSourceInput> & { active?: boolean }) => Promise<void>;
  removeSource: (id: string) => Promise<void>;
  /** One entry per source+month: creates or overwrites. amountGr=0 removes. */
  setEntry: (sourceId: string, month: string, amountGr: number, note?: string | null) => Promise<void>;
  /** "Nie było wpływu w tym miesiącu" — closes the question for that month. */
  skipMonth: (id: string, month: string) => Promise<void>;
  unskipMonth: (id: string, month: string) => Promise<void>;
  /** "Zostaw" on a day-change suggestion — that day is never proposed again. */
  dismissDaySuggestion: (id: string, day: number) => Promise<void>;
}

export const useIncomeStore = create<IncomeState>((set, get) => ({
  hydrated: false,
  sources: [],
  entries: [],

  hydrate: async () => {
    const db = await getDb();
    const sources = (await db.list<IncomeSource>(TABLES.incomeSources)).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
    const entries = await db.list<IncomeEntry>(TABLES.incomeEntries);
    set({ hydrated: true, sources, entries });
  },

  addSource: async (input) => {
    const now = new Date().toISOString();
    const source: IncomeSource = {
      id: uuid(),
      memberId: input.memberId,
      name: input.name.trim(),
      kind: input.kind,
      expectedDay: input.expectedDay,
      expectedAmountGr: input.expectedAmountGr,
      active: true,
      createdAt: now,
      updatedAt: now,
    };
    const db = await getDb();
    await db.upsert(TABLES.incomeSources, source.id, source);
    set((s) => ({ sources: [...s.sources, source] }));
    void scheduleIncomePrompt(source.id, source.name, source.expectedDay).catch(() => undefined);
    scheduleHouseholdSync();
    return source;
  },

  updateSource: async (id, patch) => {
    const current = get().sources.find((s) => s.id === id);
    if (!current) return;
    const updated: IncomeSource = { ...current, ...patch, updatedAt: new Date().toISOString() };
    const db = await getDb();
    await db.upsert(TABLES.incomeSources, id, updated);
    set((s) => ({ sources: s.sources.map((x) => (x.id === id ? updated : x)) }));
    void scheduleIncomePrompt(id, updated.name, updated.active ? updated.expectedDay : null).catch(
      () => undefined,
    );
  },

  skipMonth: async (id, month) => {
    const current = get().sources.find((s) => s.id === id);
    if (!current || (current.skippedMonths ?? []).includes(month)) return;
    const updated: IncomeSource = {
      ...current,
      skippedMonths: [...(current.skippedMonths ?? []), month],
      updatedAt: new Date().toISOString(),
    };
    const db = await getDb();
    await db.upsert(TABLES.incomeSources, id, updated);
    set((s) => ({ sources: s.sources.map((x) => (x.id === id ? updated : x)) }));
    scheduleHouseholdSync();
  },

  unskipMonth: async (id, month) => {
    const current = get().sources.find((s) => s.id === id);
    if (!current) return;
    const updated: IncomeSource = {
      ...current,
      skippedMonths: (current.skippedMonths ?? []).filter((m) => m !== month),
      updatedAt: new Date().toISOString(),
    };
    const db = await getDb();
    await db.upsert(TABLES.incomeSources, id, updated);
    set((s) => ({ sources: s.sources.map((x) => (x.id === id ? updated : x)) }));
    scheduleHouseholdSync();
  },

  dismissDaySuggestion: async (id, day) => {
    const current = get().sources.find((s) => s.id === id);
    if (!current) return;
    const updated: IncomeSource = { ...current, daySuggestionDismissed: day, updatedAt: new Date().toISOString() };
    const db = await getDb();
    await db.upsert(TABLES.incomeSources, id, updated);
    set((s) => ({ sources: s.sources.map((x) => (x.id === id ? updated : x)) }));
  },

  removeSource: async (id) => {
    const db = await getDb();
    const doomed = get().entries.filter((e) => e.sourceId === id);
    for (const e of doomed) await db.remove(TABLES.incomeEntries, e.id);
    await db.remove(TABLES.incomeSources, id);
    set((s) => ({
      sources: s.sources.filter((x) => x.id !== id),
      entries: s.entries.filter((e) => e.sourceId !== id),
    }));
    void cancelIncomePrompt(id);
  },

  setEntry: async (sourceId, month, amountGr, note = null) => {
    const db = await getDb();
    const existing = entryFor(get().entries, sourceId, month);
    if (amountGr <= 0) {
      if (existing) {
        await db.remove(TABLES.incomeEntries, existing.id);
        set((s) => ({ entries: s.entries.filter((e) => e.id !== existing.id) }));
      }
      return;
    }
    const now = new Date().toISOString();
    const entry: IncomeEntry = existing
      ? { ...existing, amountGr, note: note ?? existing.note, updatedAt: now }
      : { id: uuid(), sourceId, month, amountGr, note, createdAt: now, updatedAt: now };
    await db.upsert(TABLES.incomeEntries, entry.id, entry);
    set((s) => ({
      entries: existing ? s.entries.map((e) => (e.id === entry.id ? entry : e)) : [...s.entries, entry],
    }));
    scheduleHouseholdSync();
  },
}));
